import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode, useRef } from 'react';
import { User } from '@/types/auth';
import { AuthApi } from '@/services/api/authApi';
import { UserApi } from '@/services/api/userApi';

type AuthStatus = 'unknown' | 'authenticated' | 'unauthenticated';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  register: (userData: {
    email: string;
    username: string;
    password: string;
    age?: number;
    bio?: string;
    profile_photos?: any[];
    location_permission?: boolean;
  }) => Promise<void>;
  logout: () => Promise<void>;
  updateUserProfile: (updates: Partial<User>) => Promise<void>;
  revalidateAuth: (force?: boolean) => Promise<void>;
  isAuthenticated: boolean;
  isProfileComplete: () => boolean;
  authResolved: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Cache configuration
const AUTH_CACHE_KEY = 'auth_user_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const MIN_FETCH_INTERVAL = 10 * 1000; // 10 seconds between fetches

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authStatus, setAuthStatus] = useState<AuthStatus>('unknown');

  // Prevent multiple simultaneous requests and rate limiting
  const requestInProgress = useRef(false);
  const lastFetchTime = useRef(0);

  // Load from cache
  const loadFromCache = useCallback((): User | null => {
    try {
      const cached = localStorage.getItem(AUTH_CACHE_KEY);
      if (!cached) return null;
      
      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();
      
      if (now - timestamp < CACHE_DURATION) {
        console.log('✅ AuthContext: Using cached user data');
        return data;
      }
      
      localStorage.removeItem(AUTH_CACHE_KEY);
      return null;
    } catch (error) {
      console.error('❌ AuthContext: Cache read error:', error);
      localStorage.removeItem(AUTH_CACHE_KEY);
      return null;
    }
  }, []);

  // Save to cache
  const saveToCache = useCallback((userData: User | null) => {
    try {
      if (userData) {
        localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify({
          data: userData,
          timestamp: Date.now()
        }));
        console.log('💾 AuthContext: User data cached');
      } else {
        localStorage.removeItem(AUTH_CACHE_KEY);
        console.log('🗑️  AuthContext: Cache cleared');
      }
    } catch (error) {
      console.error('❌ AuthContext: Cache write error:', error);
    }
  }, []);

  // Fetch user with rate limiting and deduplication
  const fetchUser = useCallback(async (force: boolean = false): Promise<User | null> => {
    const now = Date.now();

    // Check if request already in progress
    if (requestInProgress.current && !force) {
      console.log('⏸️  AuthContext: Request already in progress, skipping');
      return user;
    }

    // Rate limit: Don't fetch more than once per 10 seconds (unless forced)
    if (!force && (now - lastFetchTime.current < MIN_FETCH_INTERVAL)) {
      console.log(`⏱️  AuthContext: Rate limit protection - skipping fetch (${MIN_FETCH_INTERVAL - (now - lastFetchTime.current)}ms remaining)`);
      return user;
    }

    // Check cache first (unless forced)
    if (!force) {
      const cachedUser = loadFromCache();
      if (cachedUser) {
        setUser(cachedUser);
        return cachedUser;
      }
    }

    // Fetch from API
    requestInProgress.current = true;
    lastFetchTime.current = now;

    try {
      console.log('🔄 AuthContext: Fetching user data from API');
      const userData = await AuthApi.getCurrentUser();
      console.log('✅ AuthContext: User data loaded:', { id: userData.id, username: userData.username });
      setAuthStatus('authenticated');
      setUser(userData);
      saveToCache(userData);
      return userData;
    } catch (error: any) {
      // Don't log as error if it's just a 401 (not authenticated)
      if (error?.response?.status === 401 || error?.status === 401) {
        console.log('🔐 AuthContext: Not authenticated');

        // Only set terminal state if this was a forced fetch (after login)
        // OR if we were previously authenticated (session expired)
        if (force || authStatus === 'authenticated') {
          console.log('→ Setting terminal unauthenticated state (force fetch or session expired)');
          setAuthStatus('unauthenticated');
        } else {
          console.log('→ Initial auth check failed, allowing future retry on page refresh');
          // Don't set terminal state on initial load - allow retry
          setAuthStatus('unauthenticated');
        }

        setUser(null);
        saveToCache(null);
        return null;
      } else if (error?.response?.status === 429 || error?.status === 429) {
        console.warn('⚠️  AuthContext: Rate limited! Using cached data if available');
        // Keep existing user state if rate limited
        return user;
      } else {
        console.error('❌ AuthContext: Failed to fetch user:', error);
      }
      
      setUser(null);
      saveToCache(null);
      return null;
    } finally {
      requestInProgress.current = false;
    }
  }, [user, loadFromCache, saveToCache]);

  // Initialize auth ONCE on mount
  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      try {
        console.log('🚀 AuthContext: Initializing authentication');
        
        // Try cache first
        const cachedUser = loadFromCache();
        if (cachedUser && isMounted) {
          console.log('✅ AuthContext: Initialized from cache');
          setUser(cachedUser);
          setAuthStatus('authenticated');
          setLoading(false);
          return;
        }

        // Fetch from API if no cache
        const userData = await fetchUser(false);
        if (isMounted) {
          setAuthStatus(prev => (prev === 'unknown' ? 'unauthenticated' : prev));
          setLoading(false);
        }
      } catch (error) {
        console.error('❌ AuthContext: Initialization failed:', error);
        if (isMounted) {
          setUser(null);
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
    };
  }, []); // ← ONLY runs once on mount

  const login = useCallback(async (username: string, password: string) => {
    console.log('🔐 AuthContext: Attempting login for user:', username);

    try {
      // Login sets HttpOnly cookies
      await AuthApi.login(username, password);

      // Force fetch user data (bypasses cache and rate limiting)
      const userData = await fetchUser(true);
      if (userData) {
        setAuthStatus('authenticated');
        console.log('✅ AuthContext: Login successful');
      }
    } catch (error) {
      console.error('❌ AuthContext: Login failed:', error);
      throw error;
    }
  }, [fetchUser]);

  const register = useCallback(async (userData: { 
    email: string; 
    username: string; 
    password: string;
    age?: number;
    bio?: string;
    profile_photos?: any[];
    location_permission?: boolean;
  }) => {
    try {
      console.log('📝 AuthContext: Registering user:', userData.username);
      
      const registeredUser = await AuthApi.register(userData as any);
      if (registeredUser) {
        console.log('✅ AuthContext: Registration successful');
        setUser(registeredUser);
        setAuthStatus('authenticated');
        saveToCache(registeredUser);
      } else {
        // Fallback: login with credentials
        await login(userData.username, userData.password);
      }
    } catch (error) {
      console.error('❌ AuthContext: Registration failed:', error);
      throw error;
    }
  }, [login, saveToCache]);

  const logout = useCallback(async () => {
    try {
      console.log('👋 AuthContext: Logging out user');
      await AuthApi.logout();
    } catch (error) {
      console.warn('⚠️  AuthContext: Logout API call failed:', error);
    } finally {
      setUser(null);
      saveToCache(null);
      // Reset rate limiting
      lastFetchTime.current = 0;
      requestInProgress.current = false;
    }
  }, [saveToCache]);

  const updateUserProfile = useCallback(async (updates: Partial<User>) => {
    if (!user) {
      console.error('❌ updateUserProfile: User not logged in');
      return;
    }
    
    try {
      console.log('🔄 AuthContext: Updating user profile');
      
      // Convert auth User type to UserApi User type
      const userApiUpdates: any = {};
      
      if (updates.first_name !== undefined) userApiUpdates.firstName = updates.first_name;
      if (updates.last_name !== undefined) userApiUpdates.lastName = updates.last_name;
      if (updates.bio !== undefined) userApiUpdates.bio = updates.bio;
      if (updates.avatar_url !== undefined) userApiUpdates.avatar = updates.avatar_url;
      if (updates.gender !== undefined) userApiUpdates.gender = updates.gender;
      if (updates.ethnicity !== undefined) userApiUpdates.ethnicity = updates.ethnicity;
      if (updates.age !== undefined) userApiUpdates.age = updates.age;
      if (updates.categories_of_interest !== undefined) userApiUpdates.categoriesOfInterest = updates.categories_of_interest;
      if (updates.profile_photos !== undefined) userApiUpdates.profilePhotos = updates.profile_photos;
      if (updates.location !== undefined) userApiUpdates.location = updates.location;
      if (updates.top_activities !== undefined) userApiUpdates.topActivities = updates.top_activities;
      if (updates.instagram_url !== undefined) userApiUpdates.instagramUrl = updates.instagram_url;
      if (updates.facebook_url !== undefined) userApiUpdates.facebookUrl = updates.facebook_url;
      if (updates.x_url !== undefined) userApiUpdates.xUrl = updates.x_url;
      if (updates.recovery_email !== undefined) userApiUpdates.recoveryEmail = updates.recovery_email;
      
      const updatedUser = await UserApi.updateCurrentUserProfile(userApiUpdates);
      
      // Convert back to auth User type
      const convertedUser: User = {
        id: updatedUser.id,
        email: updatedUser.email,
        username: updatedUser.username,
        created_at: updatedUser.createdAt.toISOString(),
        updated_at: updatedUser.updatedAt.toISOString(),
        first_name: updatedUser.firstName || null,
        last_name: updatedUser.lastName || null,
        bio: updatedUser.bio || null,
        avatar_url: null,
        gender: updatedUser.gender,
        ethnicity: (updatedUser as any).ethnicity || null,
        age: updatedUser.age || null,
        location: updatedUser.location as Record<string, any> | null,
        preferences: null,
        categories_of_interest: updatedUser.categoriesOfInterest || null,
        profile_photos: updatedUser.profilePhotos || null,
        top_activities: updatedUser.topActivities || null,
        instagram_url: updatedUser.instagramUrl || null,
        facebook_url: updatedUser.facebookUrl || null,
        x_url: updatedUser.xUrl || null,
        recovery_email: updatedUser.recoveryEmail ?? null,
        has_password: updatedUser.hasPassword ?? user.has_password,
      };
      
      setUser(convertedUser);
      saveToCache(convertedUser);
      console.log('✅ AuthContext: User profile updated');
    } catch (error) {
      console.error('❌ AuthContext: Failed to update profile:', error);
      throw error;
    }
  }, [user, saveToCache]);

  // CRITICAL: revalidateAuth respects rate limiting by default; pass force=true
  // to bypass it (e.g. right after a fresh session cookie was set by an OAuth/phone callback).
  const revalidateAuth = useCallback(async (force: boolean = false) => {
    console.log('🔄 AuthContext: Revalidation requested', { force });
    await fetchUser(force);
  }, [fetchUser]);

  const isProfileComplete = useCallback(() => {
    if (!user) return false;
    
    return !!(
      user.first_name || 
      user.bio || 
      (user.categories_of_interest && user.categories_of_interest.length > 0)
    );
  }, [user]);
  const authResolved = authStatus !== 'unknown';
  const isAdmin = user?.role === 'admin';

  const value: AuthContextType = useMemo(() => ({
    user,
    loading,
    login,
    register,
    logout,
    updateUserProfile,
    revalidateAuth,
    isAuthenticated: authStatus === 'authenticated',
    isProfileComplete,
    authResolved,
    isAdmin,
  }), [user, loading, login, register, logout, updateUserProfile, revalidateAuth, isProfileComplete, authResolved, isAdmin]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};